import React, { useState, useEffect } from 'react'
import { NavLink } from 'react-router-dom'
import { Lock } from 'lucide-react'
import { cls } from '../../utils/helpers.js'
import { useLanguage } from '../../i18n/index.jsx'
import NavLockedModal from './NavLockedModal.jsx'
import type { NavGroup, NavItem } from './navConstants.jsx'

interface LockedTarget {
  navKey: string | undefined
  moduleKey: string | undefined
  lockReason: string | undefined
}

interface SidebarNavGroupProps {
  group: NavGroup
  groupIsActive: (group: NavGroup) => boolean
  flyoutGroup: string | null
  setFlyoutGroup: (key: string | null) => void
  flyoutTop: number
  setFlyoutTop: (top: number) => void
  flyoutLeft: number
  setFlyoutLeft: (left: number) => void
  flyoutRef: React.RefObject<HTMLDivElement | null>
  sidebarCollapsed: boolean
}

export default function SidebarNavGroup({
  group,
  groupIsActive,
  flyoutGroup, setFlyoutGroup,
  flyoutTop, setFlyoutTop,
  flyoutLeft, setFlyoutLeft,
  flyoutRef,
  sidebarCollapsed,
}: SidebarNavGroupProps) {
  const { t } = useLanguage()
  const label = t(group.key) || group.key
  const hasActive = groupIsActive(group)
  const isOpen = flyoutGroup === group.key
  // Wave 3d — clicking a locked child opens the explanation modal.
  const [lockedTarget, setLockedTarget] = useState<LockedTarget | null>(null)

  // Escape closes the flyout — keyboard users were otherwise trapped tabbing
  // through every item to dismiss it.
  useEffect(() => {
    if (!isOpen) return
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setFlyoutGroup(null) }
    document.addEventListener('keydown', onEsc)
    return () => document.removeEventListener('keydown', onEsc)
  }, [isOpen, setFlyoutGroup])

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          if (!isOpen) {
            const rect = e.currentTarget.getBoundingClientRect()
            const aside = e.currentTarget.closest('aside')
            const margin = 8
            const itemCount = (group.children?.length || 0) + 1
            const estimatedHeight = Math.min(itemCount * 36 + 8, window.innerHeight - margin * 2)
            const maxTop = window.innerHeight - estimatedHeight - margin
            setFlyoutTop(Math.max(margin, Math.min(rect.top, maxTop)))
            setFlyoutLeft(aside ? aside.getBoundingClientRect().right : 70)
          }
          setFlyoutGroup(isOpen ? null : group.key)
        }}
        title={sidebarCollapsed ? undefined : label}
        data-sb-tip={sidebarCollapsed ? label : undefined}
        aria-label={label}
        aria-expanded={isOpen ? 'true' : 'false'}
        className={cls('nav-item', hasActive && !isOpen && 'nav-active')}
        style={{ width: '100%', border: 'none', background: hasActive && !isOpen ? undefined : 'none', cursor: 'pointer', textAlign: 'left' }}
      >
        <span className="nav-icon">{group.icon}</span>
        <span className="nav-label">{label}</span>
        {/* Chevron hidden when collapsed — it stole space and pushed the
            icon off-centre in the icon rail. */}
        {!sidebarCollapsed && (
          <span style={{ marginLeft: 'auto', opacity: 0.5, display: 'flex', fontSize: 'var(--text-sm)' }}>›</span>
        )}
      </button>
      <NavLockedModal
        open={!!lockedTarget}
        onClose={() => setLockedTarget(null)}
        navKey={lockedTarget?.navKey}
        moduleKey={lockedTarget?.moduleKey}
        lockReason={lockedTarget?.lockReason}
      />
      {isOpen && (
        <div
          ref={isOpen ? flyoutRef : undefined}
          className="sidebar-flyout"
          style={{ position: 'fixed', left: flyoutLeft + 4, top: flyoutTop }}
        >
          <div className="sidebar-flyout-title">{label}</div>
          {group.children.map((child, i) => {
            // Wave 3b — sub-group label (non-clickable section header between
            // related children). Phase 19 audit fix for OVERFULL groups.
            if ('subHeader' in child) {
              return (
                <div
                  key={`sub-${i}`}
                  className="sidebar-flyout-subheader"
                  style={{
                    padding: '6px 12px 2px',
                    fontSize: 'var(--text-tiny)',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: 'var(--text-muted)',
                  }}
                >
                  {t(child.subHeader) || child.subHeader}
                </div>
              )
            }
            const navChild = child as NavItem
            // Wave 3c — module/plan-locked item rendered as a disabled-looking
            // row with a Lock icon + "Upgrade to enable" tooltip. Phase 19
            // audit fix for "items disappear silently when modules off".
            if (navChild.locked) {
              const lockTitle = navChild.lockReason === 'plan'
                ? t('navLockedPlanTitle')
                : t('navLockedModuleTitle')
              return (
                <button
                  key={navChild.to}
                  type="button"
                  className="nav-item nav-item-locked"
                  title={lockTitle}
                  onClick={() => {
                    setFlyoutGroup(null)
                    setLockedTarget({
                      navKey: navChild.key,
                      moduleKey: navChild.moduleKey,
                      lockReason: navChild.lockReason,
                    })
                  }}
                  style={{
                    width: '100%', border: 'none', background: 'none',
                    textAlign: 'left', cursor: 'pointer',
                    opacity: 0.7,
                  }}
                >
                  {navChild.icon && <span className="nav-icon">{navChild.icon}</span>}
                  <span className="nav-label" style={{ display: 'inline' }}>
                    {t(navChild.key) || navChild.key}
                  </span>
                  <Lock
                    size={11}
                    aria-hidden="true"
                    style={{ marginLeft: 'auto', color: 'var(--text-muted)' }}
                  />
                </button>
              )
            }
            return (
              <NavLink
                key={navChild.to}
                to={navChild.to}
                end
                title={navChild.tooltip ? (t(navChild.tooltip) || navChild.tooltip) : undefined}
                className={({ isActive }) => cls('nav-item', isActive && 'nav-active')}
                onClick={() => setFlyoutGroup(null)}
              >
                {navChild.icon && <span className="nav-icon">{navChild.icon}</span>}
                <span className="nav-label" style={{ display: 'inline' }}>
                  {t(navChild.key) || navChild.key}
                </span>
                {navChild.role === 'admin' && (
                  <span
                    className="nav-admin-badge"
                    style={{
                      marginLeft: 'auto',
                      fontSize: 'var(--text-tiny)',
                      fontWeight: 600,
                      padding: '1px 6px',
                      borderRadius: 4,
                      background: 'var(--bg-overlay)',
                      color: 'var(--text-muted)',
                      letterSpacing: '0.04em',
                    }}
                  >
                    {t('navAdminBadge')}
                  </span>
                )}
              </NavLink>
            )
          })}
        </div>
      )}
    </div>
  )
}
